import React, { useState } from 'react';

import OverflowTextModal from '@rover/components/src/OverflowTextModal/';
import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import { Box, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { AvailabilityDelayEnum } from '@rover/rsdk/src/apiClient/latest/model/availabilityDelayEnum';
import type { ProviderProfileAvailabilityPreferences } from '@rover/rsdk/src/apiClient/latest/model/providerProfileAvailabilityPreferences';
import { TrainingServicePreferencesAvailabilityDelay } from '@rover/rsdk/src/apiClient/latest/model/trainingServicePreferencesAvailabilityDelay';

type AvailabilitySectionProps = {
  availabilityDelay: TrainingServicePreferencesAvailabilityDelay;
  availabilityDetails: string;
  availabilityPreferences: ProviderProfileAvailabilityPreferences;
  cancellationPolicyDescription: string;
  cancellationPolicyTranslated: string;
};

export const daysOfTheWeekDict: Record<string, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

const DayBox = ({ text, active }: { text: string; active: boolean }): JSX.Element => {
  const { colors } = useTheme();
  return (
    <Flex
      sx={active ? { backgroundColor: colors.green['300'] } : {}}
      paddingY="2x"
      borderRadius="primary"
      flexGrow="1"
      flexBasis="0"
      justifyContent="center"
    >
      <Text
        textColor={active ? 'secondary' : 'disabled'}
        size="100"
        fontWeight={active ? 'semibold' : 'regular'}
      >
        {text}
      </Text>
    </Flex>
  );
};

// TODO (DEV-104195): Translate below text after training pilot launch
// if availability delay is re-added.
const TEXT_FOR_AVAILABILTY_DELAY = {
  [AvailabilityDelayEnum['1w']]: 'Available to train now',
  [AvailabilityDelayEnum['2w']]: 'Available to train in 2 weeks',
  [AvailabilityDelayEnum['3w']]: 'Available to train in 3 weeks',
  [AvailabilityDelayEnum['1m']]: 'Available to train in 4 weeks',
  [AvailabilityDelayEnum['2m+']]: 'Available to train in 4+ weeks',
};

export const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({
  availabilityDelay,
  availabilityDetails,
  availabilityPreferences,
  cancellationPolicyDescription,
  cancellationPolicyTranslated,
}) => {
  const { colors } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // TODO (DEV-104195): Translate below text after training pilot launch
  // if availability delay is re-added.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availabilityDelayText = availabilityDelay
    ? TEXT_FOR_AVAILABILTY_DELAY[availabilityDelay]
    : 'Availability unknown';

  const openModal = (): void => {
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Flex
        flexDirection="column"
        paddingTop={['10x', '0x', '10x']}
        paddingBottom="10x"
        sx={{
          borderTop: [
            `1px solid ${colors.neutral[300]}`,
            '0px',
            `2px solid ${colors.neutral[300]}`,
          ],
        }}
      >
        <Flex flexDirection="column" data-testid="availability-section" paddingBottom="10x">
          <Heading size="400" webProps={{ responsive: false }} mb="6x">
            {/* TODO (DEV-104195): Translate below text after training pilot launch */}
            Availability
          </Heading>
          {/* Re-add the following lines to re-enable display of availability delay */}
          {/* <LineItem
            Icon={AlertSuccess}
            fill={theme.colors.text.success}
            iconSize={iconSize}
          >
            {availabilityDelayText}
          </LineItem> */}
          <OverflowTextModal numberOfLines={4} text={availabilityDetails} textColor="secondary" />
        </Flex>
        <Flex flexDirection="column" width="100%">
          <Heading size="200" mb="2x">
            Days available to train
          </Heading>
          <Flex flexDirection="row" gap="1x" width="100%">
            {Object.keys(daysOfTheWeekDict).map((day) => {
              const active = availabilityPreferences[day];
              return <DayBox key={day} text={daysOfTheWeekDict[day]} active={active} />;
            })}
          </Flex>
        </Flex>
      </Flex>

      <Flex
        width="100%"
        justifyContent="flex-start"
        alignItems="center"
        pt="2x"
        pb={['10x', '10x', '0x']}
      >
        <Text size="100" fontWeight="regular" mr="1x">
          {/* TODO (DEV-104195): Translate below text after training pilot launch */}
          Dog Training Cancellation Policy:{' '}
          <FlatButton
            onClick={openModal}
            size="100"
            fontWeight="regular"
            data-testid="cancellation-button"
          >
            {cancellationPolicyTranslated}
          </FlatButton>
        </Text>
      </Flex>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <Box width="100%" pb="3x" data-testid="cancellation-modal">
          <Heading size="300">Cancellation Policy</Heading>
          {/* @ts-expect-error children should not be required if dangerouslySetInnerHTML is used */}
          <Paragraph
            size="200"
            webProps={{
              dangerouslySetInnerHTML: {
                __html: `<strong>Dog Training: </strong> ${cancellationPolicyDescription}`,
              },
            }}
          />
        </Box>
      </Modal>
    </>
  );
};
